import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useNotify, useRefresh } from 'react-admin';
import { setVariantValidUntil, toErrorMessage } from './orderActionsApi';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../../shared/clientTime';
import { shortDateTime } from '../../shared/dateTime';

/** Najkrótszy powód zmiany terminu — jak w API (`variant-validity.ts`). */
export const MIN_VALIDITY_REASON_LENGTH = 5;

/**
 * „Zmień termin” wariantu (makieta back office v2, BR-FTL-10; decyzja
 * użytkownika 20.10.2026, D1). Nowy termin czasem polskim i powód —
 * obowiązkowy, trafia do historii zlecenia. Przycisk pokazujemy tylko temu,
 * kto może (autor wariantu albo Team Leader — `validity.canChange` z API);
 * rozstrzyga i tak serwer (403).
 */
export const VariantValidityButton = ({
  userOfferId,
  carId,
  registrationNumber,
  validUntil,
}: {
  userOfferId: number | string;
  carId: number;
  registrationNumber: string;
  /** Obecny termin (ISO); `null` = bez terminu. */
  validUntil: string | null;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(toDateTimeLocalValue(validUntil) ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const reasonOk = reason.trim().length >= MIN_VALIDITY_REASON_LENGTH;

  const save = () =>
    void (async () => {
      const at = fromDateTimeLocalValue(value);
      if (!at) return;
      setBusy(true);
      try {
        await setVariantValidUntil(userOfferId, carId, at, { reason: reason.trim() });
        notify(`Wariant ${registrationNumber} ważny do ${shortDateTime(at)}`, { type: 'success' });
        setOpen(false);
        setReason('');
        refresh();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <>
      <Button
        size="small"
        color="inherit"
        sx={{ whiteSpace: 'nowrap' }}
        data-testid={`variant-validity-change-${carId}`}
        onClick={() => {
          setValue(toDateTimeLocalValue(validUntil) ?? '');
          setOpen(true);
        }}
      >
        Zmień termin
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Termin ważności wariantu {registrationNumber}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Termin mówi, do kiedy to wstępne wskazanie jest aktualne. Po terminie
            wariant zostaje z oznaczeniem „po terminie” — nie zwalnia się sam.
            Zmiana trafi do historii zlecenia razem z powodem.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              type="datetime-local"
              size="small"
              label="Ważny do (czas polski)"
              value={value}
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              label="Powód zmiany terminu"
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
              helperText={`Wymagany, co najmniej ${MIN_VALIDITY_REASON_LENGTH} znaków — np. „klient potwierdzi jutro”`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy} color="inherit">
            Anuluj
          </Button>
          <Button onClick={save} disabled={busy || !value || !reasonOk} variant="contained">
            Zapisz termin
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
